import { LicenseType, MovingState, ProtectedEntity } from '@nirvana/shared/dtos/types/protected-entities/common';
import {
  MoveToPopUpErrorCases,
  MoveToPopUpSpecifics,
  MoveToPopUpStates,
  MoveToStates,
} from '../../../../networkPopUps/popUps/moveTo/types';
import { BaseNetworkActionHelper } from '../base.network.action.helper';
import { PopUpRenderData } from '../../../../networkPopUps/types';
import { ConsoleRequest, DataProviderEntity, DataProviderType } from '../../../../../utils/request';
import { translateFn } from '../../../../../utils/translator.helper';
import { ProtectedEntitiesHelper } from '../../protectedEntities/protected.entities.helper';

export class MoveToActionHelper extends BaseNetworkActionHelper {
  public async processAction(records: Array<ProtectedEntity>): Promise<PopUpRenderData> {
    const response: MoveToPopUpSpecifics = {
      states: {
        [MoveToPopUpErrorCases.MULTIPLE_ENTITY_TYPES_SELECTED]: false,
        [MoveToStates.NETWORK_ENTITIES_SELECTED]: false,
      },
      isError: false,
      uniqueParentIds: [],
    };

    for (const record of records) {
      if (ProtectedEntitiesHelper.isNetworkCustomFolder(record)) {
        response.states[MoveToStates.NETWORK_ENTITIES_SELECTED] = true;
        response.states[MoveToStates.NETWORK_FOLDER_SELECTED] = true;
        continue;
      }

      if (ProtectedEntitiesHelper.isComputer(record)) {
        if (!record.specifics.isManaged) {
          response.states[MoveToStates.UNMANAGED_ENTITIES_SELECTED] = true;
        }

        if (record.specifics.movingInfo && record.specifics.movingInfo.state !== MovingState.FAILED) {
          response.states[MoveToStates.ENTITIES_ALREADY_MOVING_SELECTED] = true;
        }

        response.states[MoveToStates.NETWORK_ENTITIES_SELECTED] = true;
        response.states[MoveToStates.NETWORK_COMPUTERS_SELECTED] = true;
        continue;
      }

      if (ProtectedEntitiesHelper.isCompany(record) || ProtectedEntitiesHelper.isCompanyFolder(record)) {
        response.states[MoveToStates.COMPANY_ENTITIES_SELECTED] = true;
        continue;
      }

      if (ProtectedEntitiesHelper.isContainerHostFolder(record) || ProtectedEntitiesHelper.isContainerFolder(record)) {
        response.states[MoveToStates.CONTAINER_ENTITIES_SELECTED] = true;
      }
    }

    const hasMultipleEntityClassesSelected: boolean = this.getHasMultipleEntityClassesSelected(response.states);

    if (hasMultipleEntityClassesSelected) {
      response.states[MoveToPopUpErrorCases.MULTIPLE_ENTITY_TYPES_SELECTED] = true;
      response.isError = true;
      response.errorData = {
        title: translateFn('Cannot move entities'),
        errorMessages: [translateFn('You selected different entities that cannot be moved at the same time. ')],
        buttonText: translateFn('OK'),
      };
      return response;
    }

    response.uniqueParentIds = Array.from(new Set(records.map(record => record.parentId)));

    if (
      response.states[MoveToStates.NETWORK_ENTITIES_SELECTED] ||
      response.states[MoveToStates.CONTAINER_ENTITIES_SELECTED]
    ) {
      await this.checkForEntitiesInExcluded(response);
    }

    if (!Application.Main.getUser().companyInfo.specifics.rights.manageCompanies) {
      return response;
    }

    const uniqueCompanyIds = Array.from(new Set(records.map(record => record.company?.id)));

    if (uniqueCompanyIds.length > 1) {
      // should not happen ever
      console.error('Multiple companies selected');
      return response;
    }

    const companyRecordId = uniqueCompanyIds[0];

    if (!companyRecordId) {
      throw new Error('Missing companyId from records');
    }

    const consoleResponse: {
      data: Array<ProtectedEntity>;
      success: boolean;
      total: number;
    } = await ConsoleRequest.createRequest<{
      data: Array<ProtectedEntity>;
      success: boolean;
      total: number;
    }>({
      dataProviderType: DataProviderType.CORE,
      dataProviderEntity: DataProviderEntity.MODEL,
      endpoint: 'ProtectedEntitiesEPS',
      method: 'getProtectedEntityById',
      payload: {
        protectedEntityId: companyRecordId,
        page: 1,
        limit: 1,
        selectedFields: ['specifics.licenses'],
      },
    });

    if (!consoleResponse.success) {
      throw new Error(`Failed to get company record with id ${companyRecordId}`);
    }

    const companyRecord = consoleResponse.data[0];

    if (!companyRecord.specifics.licenses) {
      throw new Error('Missing licenses from company record');
    }

    if (companyRecord.specifics.licenses.length === 1) {
      response.states[MoveToStates.TARGETS_ARE_FROM_MSP_COMPANY] =
        companyRecord.specifics.licenses[0].type === LicenseType.MONTHLY ||
        companyRecord.specifics.licenses[0].type === LicenseType.TRIAL;
    }

    return response;
  }

  private async checkForEntitiesInExcluded(response: MoveToPopUpSpecifics): Promise<void> {
    const parentIdRecords: Array<ProtectedEntity> = await this.getProtectedEntitiesTypesById(response.uniqueParentIds);

    const excludedParentIds: Array<string> = this.getExcludedParentIds(parentIdRecords);

    if (!excludedParentIds.length) {
      return;
    }
    response.states[MoveToStates.ENTITIES_FROM_DELETED_IN_SELECTED_ITEMS] = true;
  }

  private getHasMultipleEntityClassesSelected(states: MoveToPopUpStates): boolean {
    return (
      (states[MoveToStates.NETWORK_ENTITIES_SELECTED] && states[MoveToStates.COMPANY_ENTITIES_SELECTED]) ||
      (states[MoveToStates.NETWORK_ENTITIES_SELECTED] && states[MoveToStates.CONTAINER_ENTITIES_SELECTED]) ||
      (states[MoveToStates.COMPANY_ENTITIES_SELECTED] && states[MoveToStates.CONTAINER_ENTITIES_SELECTED])
    );
  }

  private async getProtectedEntitiesTypesById(parentIds: Array<string>): Promise<Array<ProtectedEntity>> {
    const consoleResponse: { data: Array<ProtectedEntity>; total: number; success: boolean } =
      await ConsoleRequest.createRequest({
        dataProviderType: DataProviderType.CORE,
        dataProviderEntity: DataProviderEntity.MODEL,
        endpoint: 'ProtectedEntitiesEPS',
        method: 'getProtectedEntityById',
        payload: {
          protectedEntityId: parentIds,
          selectedFields: ['type'],
          page: 1,
          limit: 0,
          sort: null,
          dir: null,
          filter: [],
        },
      });

    if (!consoleResponse.success) {
      throw new Error(`Failed to get entity details for ${parentIds.join(', ')}`);
    }

    return consoleResponse.data;
  }

  private getExcludedParentIds(parentEntities: Array<ProtectedEntity>): Array<string> {
    return parentEntities
      .filter(record => record.type === Constants.ProtectedEntities.EPS.TYPE_EXCLUDED_FOLDER)
      .map(record => record._id);
  }
}
